import { FormArray, FormControl, FormControlOptions, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  Announcement,
  AnnouncementStatus,
  AnnouncementType,
  ApiKey,
  ApiKeyAccessLevel,
  ApiKeyExpiration,
  ApiKeyPermission,
  ApiKeyRequest,
  ApiKeyScope,
  AutomaticProjectSetup,
  BuildingType,
  CompanySubscription,
  CompanySubscriptionStatus,
  ConstructionAreaLocation,
  FormGroupOf,
  Customer,
  Project,
  Company,
  ProjectStatus,
  PriceFactorType,
  ProjectDetails,
  UserRole,
  ProjectSettings,
  Activity,
  ConstructionArea,
  Phase,
  User,
  PriceFactor,
  ActivityTemplate,
  TradeType,
  PhaseTemplate,
  DocumentFile,
  Product,
  ProductTemplate,
  LabourCostMethod,
  QuantityPerConstructionArea,
  CostEstimateConfiguration,
  CostEstimateData,
  CostEstimateRow,
  QualityCheckItem,
  QualityCheckItemTemplate,
  QualityCheckListTemplate,
  QualityCheckListTemplateItem,
  QualityCheckItemPhase,
  ProductTemplateReminder,
  WorkUnitInTimeData,
  WorkUnit,
  QualityCheckPicture,
  WorkUnitQualityCheckList,
  WorkUnitFile,
  WorkUnitDoneQualityCheckItem,
  WorkUnitDoneQualityCheckItemStatus,
  WorkUnitInTime,
  HoursInWeekForOneEmployeePlanned,
  AssignedEmployee,
  DayOfWeek,
  TimeEntryCategory,
  TimeEntryType,
  EmployeeHours,
  EmployeeHoursStatus,
  ResetEmailRequest,
  ActivateAccountRequest,
  FormArrayOf,
  ForgotPasswordRequest,
  LoginEmailRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  Help,
  TranslationDictionary,
  Message,
  WorkingHours,
  UserType,
  LocationOnPlan,
  TimeEntryDataType,
  ServiceUnit,
  ServiceUnitInTime,
  LinkedProject,
  EmployeeHoursPeriodLock,
  EmployeeHoursPeriodLockFormModel,
  Holiday,
  CompanyMenuItem,
  ProductPackage,
  ProductPackageItem,
  ProductPackageEditItem,
  ProductPackagesMassEditRow,
  ProductPackageSummary,
  OwnershipType,
  Plan,
  PolygonPoint,
  ProjectMilestone,
  ProjectType,
  PhaseLink,
  RegisterCompany,
  StaffingUnit,
  StaffingUnitInTime,
  StaffingUnitType,
  SubPhase,
  Money,
  ProductTemplateProviderType,
  WorkTask,
  WorkTaskFormModel,
  WorkTaskPriority,
  WorkTaskStatus,
} from '@shared/models';
import { AuthService } from '@shared/services';

import { getRandomPastelColor } from './color.utils';
import { generateGuid } from './data.utils';
import { EnumExtension } from './enum.utils';
import { FormGroupValidators } from './form-validator.utils';
import { isProductPackageChild, isProductPackageParent } from './project.utils';
import { fromDayString, isEqual, toDate } from './time.utils';

export class FormGenerator {
  public static initAutomaticProjectSetupForm(): FormGroup<FormGroupOf<Omit<AutomaticProjectSetup, 'files'>>> {
    return new FormGroup<FormGroupOf<Omit<AutomaticProjectSetup, 'files'>>>({
      name: new FormControl('', Validators.required),
      tradeType: new FormControl(TradeType.Other, Validators.required),
      projectType: new FormControl(ProjectType.Other, Validators.required),
    });
  }

  public static initMessage(message?: Message): FormGroup<FormGroupOf<Message>> {
    return new FormGroup<FormGroupOf<Message>>({
      id: new FormControl(message?.id),
      conversationId: new FormControl(message?.conversationId),
      content: new FormControl(message?.content),

      files: new FormArray(message?.files?.map((x) => FormGenerator.initFileForm(x)) ?? []),
    });
  }

  public static initCustomerForm(customer: Customer | undefined): FormGroup<FormGroupOf<Customer>> {
    const form = new FormGroup<FormGroupOf<Customer>>({
      id: new FormControl(customer?.id),
      name: new FormControl(customer?.name, Validators.required),
      email: new FormControl(customer?.email, [Validators.email, Validators.minLength(5)]),
      phone: new FormControl(customer?.phone),
      addressLine1: new FormControl(customer?.addressLine1),
      addressLine2: new FormControl(customer?.addressLine2),
      city: new FormControl(customer?.city),
      postalCode: new FormControl(customer?.postalCode),
      organizationNumber: new FormControl(customer?.organizationNumber),
      contactName: new FormControl(customer?.contactName),
      contactEmail: new FormControl(customer?.contactEmail, [Validators.email, Validators.minLength(5)]),
      contactPhone: new FormControl(customer?.contactPhone),
      countryId: new FormControl(customer?.countryId),
      formattedAddress: new FormControl(customer?.formattedAddress),
      imageId: new FormControl(customer?.imageId),
    });

    if (customer) {
      form.disable();
    }

    return form;
  }

  public static initRegisterCompanyForm(translate: TranslateService): FormGroup<FormGroupOf<RegisterCompany>> {
    return new FormGroup<FormGroupOf<RegisterCompany>>({
      name: new FormControl(null, Validators.required),
      email: new FormControl(null, [Validators.required, Validators.email]),
      userCountryId: new FormControl(null, Validators.required),
      phoneNumber: new FormControl(null),
      password: new FormControl(null, [Validators.required, Validators.minLength(5)]),
      confirmPassword: new FormControl(null, [
        Validators.required,
        FormGroupValidators.requiredSameValueAsValidator(
          translate,
          'password',
          'auth.register.confirmPassword',
          'auth.register.password'
        ),
      ]),
      companyName: new FormControl(null, Validators.required),
      companyEmail: new FormControl(null, [Validators.required, Validators.email]),
      companyCountryId: new FormControl(null, Validators.required),
      companyPhoneNumber: new FormControl(null),
      companySize: new FormControl(null, Validators.required),
      calculationMethod: new FormControl(null, Validators.required),
      termsAccepted: new FormControl(false, Validators.requiredTrue),
    });
  }

  public static initProjectForm(
    authService: AuthService,
    translateService: TranslateService,
    project: Project | undefined = undefined,
    company: Company = undefined,
    disable = false
  ): FormGroup<FormGroupOf<Project>> {
    const form = new FormGroup<FormGroupOf<Project>>({
      id: new FormControl(project?.id),
      companyId: new FormControl(project?.companyId),

      ...(project?.masterProject && {
        masterProject: this.initLinkdedProjectForm(translateService, project.masterProject),
      }),
      masterProjectId: new FormControl(project?.masterProjectId),

      details: FormGenerator.initDetailsForm(translateService, authService, project?.details, company),
      settings: FormGenerator.initSettingsForm(project?.settings),
      costEstimateConfiguration: FormGenerator.initCostEstimateConfigurationForm(project?.costEstimateConfiguration),

      files: new FormArray(project?.files?.map((x) => FormGenerator.initFileForm(x)) ?? []),

      constructionsAreas: new FormArray(
        project?.constructionsAreas?.map((x) => FormGenerator.initConstructionAreasForm(translateService, x)) ?? []
      ),
      activities: new FormArray(
        project?.activities?.map((x) => FormGenerator.initActivityForm(translateService, x)) ?? []
      ),
      phases: new FormArray(project?.phases?.map((x) => FormGenerator.initPhasesForm(translateService, x)) ?? []),
      subPhases: new FormArray(
        project?.subPhases?.map((x) => FormGenerator.initSubPhaseForm(translateService, x)) ?? []
      ),
      labourCosts: new FormGroup({
        averageNettoSalaryPerHour: new FormControl(
          project?.labourCosts?.averageNettoSalaryPerHour,
          Validators.required
        ),
        socialCostsFactor: new FormControl(project?.labourCosts?.socialCostsFactor ?? 1, [
          Validators.required,
          Validators.min(0.01),
        ]),
      }),
      priceFactors: new FormArray(
        project?.priceFactors?.map((x) => FormGenerator.initPriceFactors(translateService, x)) ?? [
          FormGenerator.initPriceFactors(
            translateService,
            null,
            PriceFactorType.UnionFactor,
            translateService.instant('project.labourCosts.socialCostsFactor'),
            project?.labourCosts?.socialCostsFactor,
            true
          ),
        ]
      ),
      products: new FormArray(
        project?.products?.map((x) => FormGenerator.initProductForm(translateService, x, project)) ?? []
      ),
      hoursInWeekForOneEmployeePlanned: new FormArray(
        project?.hoursInWeekForOneEmployeePlanned?.map((x) =>
          FormGenerator.initHoursInWeekForOneEmployeePlannedForm(x)
        ) ?? []
      ),
      workUnits: new FormArray(project?.workUnits?.map((x) => FormGenerator.initWorkUnitForm(x, project)) ?? []),
      workUnitsInTime: new FormArray(
        project?.workUnitsInTime?.map((x) => FormGenerator.initWorkUnitInTimeForm(x)) ?? []
      ),
      milestones: new FormArray(project?.milestones?.map((x) => FormGenerator.initProjectMilestoneForm(x)) ?? []),
      holidays: new FormArray(project?.holidays?.map((x) => FormGenerator.initHolidayForm(x)) ?? []),
      qualityCheckItems: new FormArray(
        project?.qualityCheckItems?.map((qualityCheckItem) =>
          FormGenerator.initQualityCheckItemForm(project, qualityCheckItem, undefined, undefined, undefined)
        ) ?? []
      ),
      defaultWorkingHours: FormGenerator.initWorkingHoursForm(project?.defaultWorkingHours),
      workingHours: new FormArray(project?.workingHours?.map((x) => FormGenerator.initWorkingHoursForm(x)) ?? []),
      plans: new FormArray(project?.plans?.map((x) => FormGenerator.initPlanForm(x)) ?? []),
    });

    const disableProjectStartDate = (form: FormGroup<FormGroupOf<Project>>) => {
      if (form?.controls?.workUnitsInTime?.length || form.disabled) {
        form?.controls?.details?.controls?.startDate?.disable({
          onlySelf: true,
          emitEvent: false,
        });
      } else if (form.enabled) {
        form?.controls?.details?.controls?.startDate?.enable({
          onlySelf: true,
          emitEvent: false,
        });
      }
    };

    form.controls?.workUnitsInTime?.valueChanges.subscribe(() => disableProjectStartDate(form));

    form.controls.labourCosts.controls.socialCostsFactor?.valueChanges.subscribe(() =>
      updateSocialCostFactorLaborCost(translateService, form)
    );

    if (disable) {
      form.disable();
    }

    return form;
  }

  public static initLinkdedProjectForm(
    translateService: TranslateService,
    linkedProject: LinkedProject
  ): FormGroup<FormGroupOf<LinkedProject>> {
    const form = new FormGroup<FormGroupOf<LinkedProject>>({
      id: new FormControl(linkedProject?.id),
      companyId: new FormControl(linkedProject?.companyId),
      companyName: new FormControl(linkedProject?.name),
      name: new FormControl(linkedProject?.name),
      status: new FormControl(linkedProject?.status),
      color: new FormControl(linkedProject?.color),
      constructionsAreas: new FormArray(
        linkedProject?.constructionsAreas?.map((x) => FormGenerator.initConstructionAreasForm(translateService, x)) ??
          []
      ),
      activities: new FormArray(
        linkedProject?.activities?.map((x) => FormGenerator.initActivityForm(translateService, x)) ?? []
      ),
      phases: new FormArray(linkedProject?.phases?.map((x) => FormGenerator.initPhasesForm(translateService, x)) ?? []),
    });

    return form;
  }

  public static initDetailsForm(
    translateService: TranslateService,
    authService: AuthService,
    item: ProjectDetails | undefined = undefined,
    company: Company = undefined
  ): FormGroup<FormGroupOf<ProjectDetails>> {
    return new FormGroup<FormGroupOf<ProjectDetails>>({
      id: new FormControl(item?.id),
      name: new FormControl(item?.name ?? translateService.instant('project.details.newProject'), Validators.required),
      projectNumber: new FormControl(item?.projectNumber ?? null),
      color: new FormControl(item?.color ?? null),
      startDate: new FormControl(item?.startDate ? toDate(item.startDate) : null, Validators.required),
      endDate: new FormControl(item?.endDate ? toDate(item.endDate) : null, [
        Validators.required,
        FormGroupValidators.requiredLoverValueAsValidator(
          translateService,
          'startDate',
          'project.details.startDate',
          'project.details.endDate'
        ),
      ]),
      tenderDeadline: new FormControl(item?.tenderDeadline ? toDate(item.tenderDeadline) : null),
      notes: new FormControl(item?.notes ?? null),
      status: new FormControl(item?.status ?? ProjectStatus.Draft),
      type: new FormControl(item?.type ?? ProjectType.Other, Validators.required),
      buildingType: new FormControl(item?.buildingType ?? BuildingType.Other),
      ownershipType: new FormControl(item?.ownershipType ?? OwnershipType.Unknown),
      menuNotes: new FormControl(item?.menuNotes ?? null),
      customerId: new FormControl(item?.customerId),
      currency: new FormControl(item?.currency ?? company?.defaultCurrency, Validators.required),
      isMetric: new FormControl(
        item?.isMetric ?? company?.isMetric ?? item?.country?.isMetric ?? company?.country?.isMetric ?? true
      ),
      tradeTypes: new FormControl(item?.tradeTypes ?? company?.tradeTypes ?? [], Validators.required),

      contactName: new FormControl(item?.contactName ?? null),
      contactEmail: new FormControl(item?.contactEmail ?? null, [Validators.email]),
      contactPhone: new FormControl(item?.contactPhone ?? null),

      addressLine1: new FormControl(item?.addressLine1 ?? null),
      addressLine2: new FormControl(item?.addressLine2 ?? null),
      city: new FormControl(item?.city ?? null),
      postalCode: new FormControl(item?.postalCode ?? null),
      state: new FormControl(item?.state ?? null),
      countryId: new FormControl(item?.countryId ?? null),
      imageId: new FormControl(item?.imageId ?? null),

      responsibleProjectManagerUserId: new FormControl(
        item?.responsibleProjectManagerUserId ??
          (!authService.isUserInOneOfRoles([UserRole.Admin, UserRole.Agent]) ? authService.user?.id : null),
        Validators.required
      ),
      assistantProjectManagersUserIds: new FormControl(item?.assistantProjectManagersUserIds ?? []),
    });
  }

  public static initSettingsForm(
    item: ProjectSettings | undefined = undefined
  ): FormGroup<FormGroupOf<ProjectSettings>> {
    return new FormGroup<FormGroupOf<ProjectSettings>>({
      constructionAreasAlias: new FormControl(item?.constructionAreasAlias ?? null),
      activitiesAlias: new FormControl(item?.activitiesAlias ?? null),
      phasesAlias: new FormControl(item?.phasesAlias ?? null),
      hasSubPhases: new FormControl(item?.hasSubPhases ?? null),
    });
  }

  public static initActivityForm(
    translateService: TranslateService,
    item: Activity | undefined = undefined
  ): FormGroup<FormGroupOf<Activity>> {
    return new FormGroup<FormGroupOf<Activity>>({
      id: new FormControl(item?.id ?? generateGuid()), //TODO: change id generation, create activity model
      order: new FormControl(item?.order ?? 0),
      name: new FormControl(item?.name ?? null, [
        Validators.required,
        FormGroupValidators.hasDuplicateInFormArrayOfFormGroupValidator(translateService, 'name'),
      ]),
      masterActivityIds: new FormControl(item?.masterActivityIds ?? []),
      markup: new FormControl(item?.markup ?? null),
      color: new FormControl(item?.color ?? getRandomPastelColor()),
      outsideOfContract: new FormControl(item?.outsideOfContract ?? false),
      correctionHourlyCost: new FormControl(item?.correctionHourlyCost ?? null),
      correctionMaterial: new FormControl(item?.correctionMaterial ?? null),
      hiddenPhaseIds: new FormControl(item?.hiddenPhaseIds ?? []),
    });
  }

  public static initConstructionAreasForm(
    translateService: TranslateService,
    item: ConstructionArea | undefined = undefined
  ): FormGroup<FormGroupOf<ConstructionArea>> {
    return new FormGroup({
      id: new FormControl(item?.id ?? generateGuid()), //TODO: change id generation, create activity model
      order: new FormControl(item?.order ?? 0),
      name: new FormControl(item?.name ?? null, [
        Validators.required,
        FormGroupValidators.hasDuplicateInFormArrayOfFormGroupValidator(translateService, 'name'),
      ]),
      masterConstructionAreaIds: new FormControl(item?.masterConstructionAreaIds ?? []),
      size: new FormControl(item?.size ?? 1, Validators.required),
      color: new FormControl(item?.color ?? getRandomPastelColor()),
      files: new FormArray(item?.files?.map((x) => FormGenerator.initFileForm(x)) ?? []),
    });
  }

  public static initPhasesForm(translateService: TranslateService, item: Phase | undefined = undefined): FormGroup {
    return new FormGroup<FormGroupOf<Phase>>({
      id: new FormControl(item?.id ?? generateGuid()), //TODO: change id generation, create activity model
      order: new FormControl(item?.order ?? 0),
      code: new FormControl(item?.code),
      name: new FormControl(item?.name, [
        Validators.required,
        FormGroupValidators.hasDuplicateInFormArrayOfFormGroupValidator(translateService, 'name'),
      ]),
      color: new FormControl(item?.color ?? getRandomPastelColor()),
      masterPhaseIds: new FormControl(item?.masterPhaseIds ?? []),
      linkedPhaseIds: new FormControl(item?.linkedPhaseIds ?? []),
    });
  }

  public static initSubPhaseForm(
    translateService: TranslateService,
    item: SubPhase | undefined = undefined
  ): FormGroup<FormGroupOf<SubPhase>> {
    return new FormGroup<FormGroupOf<SubPhase>>({
      id: new FormControl(item?.id ?? generateGuid()),
      order: new FormControl(item?.order ?? 0),
      code: new FormControl(item?.code),
      name: new FormControl(item?.name, [
        Validators.required,
        FormGroupValidators.hasDuplicateInFormArrayOfFormGroupValidator(translateService, 'name'),
      ]),
      color: new FormControl(item?.color ?? getRandomPastelColor()),
    });
  }

  public static initUserForm(user?: User, companyId?: string): FormGroup<FormGroupOf<User>> {
    return new FormGroup<FormGroupOf<User>>({
      id: new FormControl(user?.id),
      type: new FormControl(user?.type ?? UserType.unknown),
      email: new FormControl(
        {
          value: user?.email ?? null,
          disabled: !!user,
        },
        [Validators.required, Validators.email]
      ),
      employeeNumber: new FormControl(user?.employeeNumber),
      firstName: new FormControl(user?.firstName),
      lastName: new FormControl(user?.lastName),
      fullName: new FormControl(user?.fullName),
      phoneNumber: new FormControl(user?.phoneNumber),
      addressLine1: new FormControl(user?.addressLine1),
      addressLine2: new FormControl(user?.addressLine2),
      postalCode: new FormControl(user?.postalCode),
      city: new FormControl(user?.city),
      state: new FormControl(user?.state),
      nextOfKinName: new FormControl(user?.nextOfKinName),
      nextOfKinPhoneNumber: new FormControl(user?.nextOfKinPhoneNumber),
      bankAccountNumber: new FormControl(user?.bankAccountNumber),
      personalNumber: new FormControl(user?.personalNumber),
      financialInfo: new FormControl(user?.financialInfo),
      roles: new FormControl(user?.roles ?? UserRole.Worker, Validators.required),
      isAssignableEmployee: new FormControl(user ? user.isAssignableEmployee : true),

      languageId: new FormControl(user?.languageId),
      startsAt: new FormControl(user?.startsAt ? toDate(user.startsAt) : toDate(), Validators.required),
      endsAt: new FormControl(user?.endsAt ? toDate(user.endsAt) : null),

      imageId: new FormControl(user?.imageId),
      countryId: new FormControl(user?.countryId),
      companyId: new FormControl(user?.companyId ?? companyId),
      timeZone: new FormControl(user?.timeZone),
      files: new FormArray(user?.files?.map((x) => FormGenerator.initFileForm(x)) ?? []),
    });
  }

  public static initCompanyForm(
    translateService: TranslateService,
    company: Company | null = null,
    disable = true
  ): FormGroup<FormGroupOf<Company>> {
    const form = new FormGroup<FormGroupOf<Company>>({
      id: new FormControl(company?.id ?? null),
      name: new FormControl(company?.name ?? null, Validators.required),
      email: new FormControl(company?.email, Validators.required),
      phoneNumber: new FormControl(company?.phoneNumber ?? null),

      agentId: new FormControl(company?.agentId ?? null),
      addressLine1: new FormControl(company?.addressLine1 ?? null, Validators.required),
      addressLine2: new FormControl(company?.addressLine2 ?? null),
      postalCode: new FormControl(company?.postalCode ?? null, Validators.required),
      state: new FormControl(company?.state),
      isMetric: new FormControl(company?.isMetric ?? company?.country?.isMetric ?? company?.country?.isMetric ?? true),
      city: new FormControl(company?.city ?? null, Validators.required),
      countryId: new FormControl(company?.countryId ?? null, Validators.required),
      formattedAddress: new FormControl(company?.formattedAddress),
      monthlyPayDate: new FormControl(company?.monthlyPayDate),
      remindNotApprovedHours: new FormControl(company?.remindNotApprovedHours ?? false),
      remindNotApprovedHoursDate: new FormControl(
        company?.remindNotApprovedHoursDate,
        FormGroupValidators.conditionalValidator<Company>(
          (company) => company?.remindNotApprovedHours,
          [Validators.required, Validators.min(1), Validators.max(31)],
          (x) => x.controls.remindNotApprovedHours
        )
      ),
      defaultCurrency: new FormControl(company?.defaultCurrency ?? null, Validators.required),
      tradeTypes: new FormControl(company?.tradeTypes ?? [], Validators.required),

      isActive: new FormControl(company?.isActive ?? true),
      imageId: new FormControl(company?.imageId ?? null),
      ...(company?.priceFactors && {
        priceFactors: new FormArray(
          company?.priceFactors?.map((x) => FormGenerator.initPriceFactors(translateService, x)) ?? [
            FormGenerator.initPriceFactors(
              translateService,
              null,
              PriceFactorType.UnionFactor,
              translateService.instant('project.labourCosts.socialCostsFactor'),
              company?.labourCosts?.socialCostsFactor,
              true
            ),
          ]
        ),
      }),
      ...(company?.labourCosts && {
        labourCosts: new FormGroup({
          averageNettoSalaryPerHour: new FormControl(
            company?.labourCosts?.averageNettoSalaryPerHour ?? { amount: 0, currency: company?.defaultCurrency ?? 0 },
            Validators.required
          ),
          socialCostsFactor: new FormControl(company?.labourCosts?.socialCostsFactor ?? 1, [
            Validators.required,
            Validators.min(0.01),
          ]),
        }),
      }),

      ...(company?.estimateParameters && {
        estimateParameters: new FormGroup({
          defaultOverhead: new FormControl(company?.estimateParameters?.defaultOverhead ?? 0, [
            Validators.required,
            Validators.min(0),
          ]),
          defaultMarkup: new FormControl(company?.estimateParameters?.defaultMarkup ?? 0, [
            Validators.required,
            Validators.min(0),
          ]),
        }),
      }),

      ...(company?.activityTemplates && {
        activityTemplates: new FormArray(
          company?.activityTemplates?.map((activityTemplate) =>
            FormGenerator.initActivityTemplateForm(activityTemplate)
          ) ?? []
        ),
      }),

      ...(company?.phaseTemplates && {
        phaseTemplates: new FormArray(
          company?.phaseTemplates?.map((activityTemplate) => FormGenerator.initPhaseTemplateForm(activityTemplate)) ??
            []
        ),
      }),
      ...(company?.menuItems && {
        menuItems: new FormArray(company?.menuItems?.map((item) => FormGenerator.initMenuItemsForm(item)) ?? []),
      }),
      ...(company?.defaultWorkingHours && {
        defaultWorkingHours: FormGenerator.initWorkingHoursForm(company?.defaultWorkingHours),
      }),
      ...(company?.subscription && {
        subscription: FormGenerator.initCompanySubscriptionForm(company?.subscription, null, disable),
      }),
      ...(company?.workingHours && {
        workingHours: new FormArray(company?.workingHours?.map((x) => FormGenerator.initWorkingHoursForm(x)) ?? []),
      }),
    });

    if (company && disable) {
      form.disable();
    }

    form.controls.labourCosts?.controls?.socialCostsFactor?.valueChanges.subscribe(() =>
      updateSocialCostFactorLaborCost(translateService, form)
    );
    return form;
  }

  public static initMenuItemsForm(item?: CompanyMenuItem, parentId?: string): FormGroup<FormGroupOf<CompanyMenuItem>> {
    return new FormGroup<FormGroupOf<CompanyMenuItem>>({
      id: new FormControl(item?.id ?? generateGuid()),
      label: FormGenerator.initTranslationDictionaryForm(item?.label, undefined),
      url: new FormControl(item?.url),
      routerLink: new FormControl(item?.routerLink),
      icon: new FormControl(item?.icon),
      imgSrc: new FormControl(item?.imgSrc),
      companyId: new FormControl(item?.companyId),
      parentId: new FormControl(item?.parentId ?? parentId),
      items: new FormArray(item?.items?.map((item) => FormGenerator.initMenuItemsForm(item)) ?? []),
    });
  }

  public static initPriceFactors(
    translateService: TranslateService,
    item: PriceFactor | undefined = undefined,
    type: PriceFactorType | undefined = undefined,
    name: string | undefined = undefined,
    factor: number | undefined = undefined,
    readonly: boolean = false
  ): FormGroup<FormGroupOf<PriceFactor>> {
    const form = new FormGroup({
      id: new FormControl(item?.id ?? generateGuid()), //TODO: change id generation, create activity model
      order: new FormControl(item?.order ?? 0),
      name: new FormControl(item?.name ?? name, [
        Validators.required,
        FormGroupValidators.hasDuplicateInFormArrayOfFormGroupValidator(translateService, 'name'),
      ]),
      type: new FormControl(item?.type ?? type ?? PriceFactorType.ComplexityFactor, Validators.required),
      factor: new FormControl(item?.factor ?? factor, [
        Validators.required,
        FormGroupValidators.conditionalValidator<PriceFactor>(
          (typeValue) => !!typeValue && typeValue.type !== PriceFactorType.ComplexityFactor,
          Validators.min(0),
          (x) => x.controls.type
        ),
      ]),
      readonly: new FormControl(item?.readonly ?? readonly),
      lastModifiedById: new FormControl(item?.lastModifiedById),
      lastModified: new FormControl(item?.lastModified),
    });

    if (form.value.readonly) {
      form.disable({
        onlySelf: true,
        emitEvent: false,
      });
    }

    return form;
  }

  public static initActivityTemplateForm(
    item?: ActivityTemplate,
    order: number = 0
  ): FormGroup<FormGroupOf<ActivityTemplate>> {
    return new FormGroup<FormGroupOf<ActivityTemplate>>({
      id: new FormControl(item?.id),
      name: new FormControl(item?.name),
      tradeType: new FormControl(item?.tradeType ?? TradeType.Other),
      order: new FormControl(item?.order ?? order ?? 0),
      lastModifiedById: new FormControl(item?.lastModifiedById),
      lastModified: new FormControl(item?.lastModified),
    });
  }

  public static initPhaseTemplateForm(item?: PhaseTemplate, order: number = 0): FormGroup<FormGroupOf<PhaseTemplate>> {
    return new FormGroup<FormGroupOf<PhaseTemplate>>({
      id: new FormControl(item?.id),
      name: new FormControl(item?.name),
      tradeType: new FormControl(item?.tradeType ?? TradeType.Other),
      order: new FormControl(item?.order ?? order ?? 0),
      lastModifiedById: new FormControl(item?.lastModifiedById),
      lastModified: new FormControl(item?.lastModified),
    });
  }

  public static initEmployeeHoursPeriodLockForm(
    item?: EmployeeHoursPeriodLock
  ): FormGroup<FormGroupOf<EmployeeHoursPeriodLockFormModel>> {
    const form = new FormGroup<FormGroupOf<EmployeeHoursPeriodLockFormModel>>({
      id: new FormControl(item?.id),
      startsAt: new FormControl(item?.startsAt),
      endsAt: new FormControl(item?.endsAt),
      range: new FormControl(item ? [toDate(item?.startsAt), toDate(item?.endsAt)] : null),
      reason: new FormControl(item?.reason),
    });

    form.controls.range.valueChanges.subscribe((range) => {
      const [startsAt, endsAt] = range ?? [null, null];
      form.controls.startsAt.patchValue(startsAt, { emitEvent: false });
      form.controls.endsAt.patchValue(endsAt, { emitEvent: false });
    });

    return form;
  }

  public static initHolidayForm(item?: Holiday): FormGroup<FormGroupOf<Holiday>> {
    const form = new FormGroup<FormGroupOf<Holiday>>({
      id: new FormControl(item?.id),
      name: new FormControl(item?.name, [Validators.required, Validators.maxLength(200)]),
      from: new FormControl(item?.from, Validators.required),
      to: new FormControl(item?.to, Validators.required),
    });

    form.controls.from.valueChanges.subscribe((from) => {
      if (from && (!form.controls.to.value || form.controls.to.value < from)) {
        form.controls.to.patchValue(from);
      }
    });

    return form;
  }

  public static initFileForm(item?: DocumentFile): FormGroup<FormGroupOf<DocumentFile>> {
    return new FormGroup<FormGroupOf<DocumentFile>>({
      id: new FormControl(item?.id),
      fileName: new FormControl(item?.fileName),
      description: new FormControl(item?.description),
      summary: new FormControl(item?.summary),
      userSummary: new FormControl(item?.userSummary),
      isSummaryProcessing: new FormControl(item?.isSummaryProcessing ?? false),
      path: new FormControl(item?.path),
      url: new FormControl(item?.url),
      thumbnailUrl: new FormControl(item?.thumbnailUrl),
      contentType: new FormControl(item?.contentType),
    });
  }

  public static initProductForm(
    translateService: TranslateService | undefined,
    product?: Product | ProductTemplate,
    project?: Project,
    phaseId?: string,
    activityId?: string,
    order?: number,
    newItem = false
  ): FormGroup<FormGroupOf<Product>> {
    const form = new FormGroup<FormGroupOf<Product>>({
      id: new FormControl(newItem ? generateGuid() : product?.id),
      created: new FormControl(newItem ? null : product?.created),
      packageAssignmentGuid: new FormControl((product as Product)?.packageAssignmentGuid),
      isPackageParent: new FormControl((product as Product)?.isPackageParent ?? false),
      sourcePackageId: new FormControl((product as Product)?.sourcePackageId),
      sourcePackageGuid: new FormControl((product as Product)?.sourcePackageGuid),
      sourcePackageName: new FormControl((product as Product)?.sourcePackageName),
      sourcePackageCode: new FormControl((product as Product)?.sourcePackageCode),
      sourcePackageItemId: new FormControl((product as Product)?.sourcePackageItemId),
      packageItemQuantity: new FormControl((product as Product)?.packageItemQuantity),
      phaseId: new FormControl(phaseId ?? (product as Product)?.phaseId),
      subPhaseId: new FormControl((product as Product)?.subPhaseId),
      activityId: new FormControl(activityId ?? (product as Product)?.activityId),
      productTemplateId: new FormControl((product as Product)?.productTemplateId),

      name: new FormControl(
        product?.name ?? translateService?.instant('project.defaults.newProductName'),
        Validators.required
      ),
      standalone: new FormControl(
        { value: (product as Product)?.standalone ?? false, disabled: false },
        Validators.required
      ),
      unitCost: new FormControl(product?.unitCost),
      referenceCode: new FormControl(product?.referenceCode),
      skillsRequired: new FormControl(product?.skillsRequired),
      specialTools: new FormControl(product?.specialTools),
      supplier: new FormControl(product?.supplier),
      supplierCode: new FormControl(product?.supplierCode),
      unionCode: new FormControl(product?.unionCode),
      unionName: new FormControl(product?.unionName),
      notes: new FormControl(product?.notes),
      order: new FormControl(order ?? (product as Product)?.order ?? 0, Validators.required),

      unitOfMeasure: new FormControl(product?.unitOfMeasure ?? translateService?.instant('project.defaults.units')),
      type: new FormControl(product?.type),
      additionalCostsPercentage: new FormControl(product?.additionalCostsPercentage ?? 0, [Validators.min(0)]),
      additionalSubcontractorCosts: new FormControl(product?.additionalSubcontractorCosts),
      specialTreatmentCosts: new FormControl(product?.specialTreatmentCosts),
      additionalLabourCostsPercentage: new FormControl(product?.additionalLabourCostsPercentage ?? 0),
      quantityDocumentation: new FormControl((product as Product)?.quantityDocumentation ?? 0),

      labourCostMethod: new FormControl(
        product?.labourCostMethod ?? LabourCostMethod.InstallationTime,
        Validators.required
      ),
      baseUnitTime: new FormControl(product?.baseUnitTime ?? 0, Validators.required),
      baseSalaryPerUnit: new FormControl(product?.baseSalaryPerUnit),

      quantityPerConstructionAreas: new FormArray(
        this.initQuantityPerConstructionAreasFormArray(product as Product, project)
      ),
    });

    const updateLabourCostsDependentControls = (labourCostMethod: LabourCostMethod) => {
      updateLabourCostsMethodControls(form.controls, form.enabled, labourCostMethod);
    };

    const updateStandaloneControl = () => {
      const hasQuantityGreaterThanOne = form.controls.quantityPerConstructionAreas.controls.some(
        (area) => area.value.quantity > 0
      );

      if (hasQuantityGreaterThanOne) {
        form.controls.standalone.disable({ onlySelf: true, emitEvent: false });
        form.controls.standalone.setValue(false, { onlySelf: true, emitEvent: false });
      } else if (form.enabled) {
        form.controls.standalone.enable({ onlySelf: true, emitEvent: false });
      }
    };

    const disableProductPackageFields = () => {
      const productData = form.getRawValue();
      if (isProductPackageParent(productData)) {
        const editableFields: (keyof Product)[] = [
          'referenceCode',
          'name',
          'skillsRequired',
          'specialTools',
          'supplier',
          'supplierCode',
          'unitOfMeasure',
          'quantityDocumentation',
          'quantityPerConstructionAreas',
        ];

        for (const [field, control] of Object.entries(form.controls)) {
          if (!editableFields.includes(field as keyof Product)) {
            control.disable({ emitEvent: false });
          }
        }
      }

      if (isProductPackageChild(productData)) {
        const disabledFields: (keyof Product)[] = ['quantityPerConstructionAreas'];

        for (const [field, control] of Object.entries(form.controls)) {
          if (disabledFields.includes(field as keyof Product)) {
            control.disable({ emitEvent: false });
          }
        }
      }
    };

    form.controls.quantityPerConstructionAreas.valueChanges.subscribe(() => {
      updateStandaloneControl();
    });

    form.controls.labourCostMethod.valueChanges.subscribe((labourCostMethod) => {
      updateLabourCostsDependentControls(labourCostMethod);
    });

    updateLabourCostsDependentControls(form.controls.labourCostMethod.value);
    updateStandaloneControl();
    disableProductPackageFields();

    const originalEnable = form.enable.bind(form);
    form.enable = () => {
      originalEnable();

      updateLabourCostsDependentControls(form.controls.labourCostMethod.value);
      updateStandaloneControl();
      disableProductPackageFields();
    };

    return form;
  }

  private static initQuantityPerConstructionAreasFormArray(
    product: Product | undefined,
    project: Project | undefined
  ): FormGroup<FormGroupOf<QuantityPerConstructionArea>>[] {
    const existingEntries = product?.quantityPerConstructionAreas ?? [];
    const constructionAreas = project?.constructionsAreas ?? [];
    const quantitiesByConstructionAreaId = new Map<string, QuantityPerConstructionArea>();
    for (const entry of existingEntries) {
      quantitiesByConstructionAreaId.set(entry.constructionAreaId, entry);
    }

    return constructionAreas.map((constructionArea) =>
      quantitiesByConstructionAreaId.has(constructionArea.id)
        ? this.initQuantityPerConstructionAreasForm(quantitiesByConstructionAreaId.get(constructionArea.id))
        : this.initQuantityPerConstructionAreasForm(null, constructionArea.id)
    );
  }

  private static initQuantityPerConstructionAreasForm(
    quantityPerConstructionArea: QuantityPerConstructionArea,
    constructionAreaId?: string
  ): FormGroup<FormGroupOf<QuantityPerConstructionArea>> {
    const form = new FormGroup({
      id: new FormControl(quantityPerConstructionArea?.id),
      constructionAreaId: new FormControl(
        quantityPerConstructionArea?.constructionAreaId ?? constructionAreaId,
        Validators.required
      ),
      quantity: new FormControl(quantityPerConstructionArea?.quantity ?? 0),
    });
    return form;
  }

  public static initCostEstimateConfigurationForm(
    costEstimateConfiguration?: CostEstimateConfiguration
  ): FormGroup<FormGroupOf<CostEstimateConfiguration>> {
    const form = new FormGroup<FormGroupOf<CostEstimateConfiguration>>({
      id: new FormControl(costEstimateConfiguration?.id ?? null),
      generalOverhead: new FormControl(costEstimateConfiguration?.generalOverhead ?? null),
      productMaterialCostsOverhead: new FormControl(costEstimateConfiguration?.productMaterialCostsOverhead ?? null),
      additionalCostsOverhead: new FormControl(costEstimateConfiguration?.additionalCostsOverhead ?? null),
      additionalSubcontractorCostsOverhead: new FormControl(
        costEstimateConfiguration?.additionalSubcontractorCostsOverhead ?? null
      ),
      specialTreatmentCostsOverhead: new FormControl(costEstimateConfiguration?.specialTreatmentCostsOverhead ?? null),
      salaryOverhead: new FormControl(costEstimateConfiguration?.salaryOverhead ?? null),
    });

    form.disable();

    return form;
  }

  public static initCostEstimateForm(costEstimateData?: CostEstimateData): FormGroup<FormGroupOf<CostEstimateData>> {
    const rowForms = costEstimateData?.rows?.map((row) => this.initCostEstimateRowForm(row)) ?? [];

    const form = new FormGroup<FormGroupOf<CostEstimateData>>({
      configuration: this.initCostEstimateConfigurationForm(costEstimateData?.configuration),
      rows: new FormArray(rowForms),
      insideContractSummary: this.initCostEstimateRowForm(costEstimateData?.insideContractSummary),
      outsideOfContractSummary: this.initCostEstimateRowForm(costEstimateData?.outsideOfContractSummary),
      totalSummary: this.initCostEstimateRowForm(costEstimateData?.totalSummary),
    });
    form.disable();
    return form;
  }

  public static initCostEstimateRowForm(row?: CostEstimateRow): FormGroup<FormGroupOf<CostEstimateRow>> {
    return new FormGroup<FormGroupOf<CostEstimateRow>>({
      activityId: new FormControl(row?.activityId),
      phaseId: new FormControl(row?.phaseId),
      name: new FormControl(row?.name),
      totalProductMaterialCosts: new FormControl(row?.totalProductMaterialCosts),
      totalAdditionalCosts: new FormControl(row?.totalAdditionalCosts),
      totalAdditionalSubcontractorCosts: new FormControl(row?.totalAdditionalSubcontractorCosts),
      totalSpecialTreatmentCosts: new FormControl(row?.totalSpecialTreatmentCosts),
      totalAdditionalLabourCosts: new FormControl(row?.totalAdditionalLabourCosts),
      totalSalary: new FormControl(row?.totalSalary),
      totalInstallationCosts: new FormControl(row?.totalInstallationCosts),
      totalOverhead: new FormControl(row?.totalOverhead),
      totalProjectCosts: new FormControl(row?.totalProjectCosts),
      markup: new FormControl(row?.markup),
      salesPrice: new FormControl(row?.salesPrice),
      salesPricePerAreaUnit: new FormControl(row?.salesPricePerAreaUnit),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      salesPriceConstructionArea: new FormControl(row?.salesPriceConstructionArea) as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      salesPriceConstructionAreaPerAreaUnit: new FormControl(row?.salesPriceConstructionAreaPerAreaUnit) as any,
      profitMargin: new FormControl(row?.profitMargin),
      profitMarginPercent: new FormControl(row?.profitMarginPercent),
      correctionHourlyCost: new FormControl(row?.correctionHourlyCost),
      correctionMaterial: new FormControl(row?.correctionMaterial),
      prognosisMargin: new FormControl(row?.prognosisMargin),
      prognosisMarginPercent: new FormControl(row?.prognosisMarginPercent),
      isOutsideOfContract: new FormControl(row?.isOutsideOfContract),
      isSummaryRow: new FormControl(row?.isSummaryRow),
    });
  }

  public static initQualityCheckItemForm(
    project: Project | undefined,
    checkItem: QualityCheckItem | undefined,
    qualityCheckItemTemplate: QualityCheckItemTemplate | undefined,
    activityId: string | undefined,
    languageIds: string[] | undefined,
    order?: number
  ): FormGroup<FormGroupOf<QualityCheckItem>> {
    const form = new FormGroup<FormGroupOf<QualityCheckItem>>({
      id: new FormControl(checkItem?.id ?? generateGuid()),
      qualityCheckItemTemplateId: new FormControl(
        checkItem?.qualityCheckItemTemplateId ?? qualityCheckItemTemplate?.id
      ),
      name: new FormControl(checkItem?.name ?? qualityCheckItemTemplate?.name),
      nameTranslation: checkItem
        ? FormGenerator.initTranslationDictionaryForm(checkItem?.nameTranslation, languageIds)
        : FormGenerator.initTranslationDictionaryForm(qualityCheckItemTemplate?.nameTranslation, languageIds),
      code: new FormControl(checkItem?.code ?? qualityCheckItemTemplate?.code, [Validators.required]),
      comment: new FormControl(checkItem?.comment ?? qualityCheckItemTemplate?.comment),
      commentTranslation: checkItem
        ? FormGenerator.initTranslationDictionaryForm(checkItem?.commentTranslation, languageIds)
        : FormGenerator.initTranslationDictionaryForm(qualityCheckItemTemplate?.commentTranslation, languageIds),
      reference: new FormControl(checkItem?.reference ?? qualityCheckItemTemplate?.reference),
      referenceTranslation: checkItem
        ? FormGenerator.initTranslationDictionaryForm(checkItem?.referenceTranslation, languageIds)
        : FormGenerator.initTranslationDictionaryForm(qualityCheckItemTemplate?.referenceTranslation, languageIds),
      activityId: new FormControl(checkItem?.activityId ?? activityId),
      qualityCheckItemPhases: new FormArray(this.initQualityCheckItemPhaseFormArray(project, checkItem)),
      order: new FormControl(checkItem?.order ?? order ?? 0),
    });
    return form;
  }

  private static initQualityCheckItemPhaseFormArray(
    project: Project | undefined,
    qualityCheckItem: QualityCheckItem | undefined
  ): FormGroup<FormGroupOf<QualityCheckItemPhase>>[] {
    const forms =
      qualityCheckItem?.qualityCheckItemPhases?.map((qualityCheckItemPhase) =>
        this.initQualityCheckItemPhaseForm(qualityCheckItemPhase)
      ) ?? [];
    const missingPhases =
      project?.phases
        ?.filter((phase) => !forms.some((x) => x.value.phaseId === phase.id))
        .map((phase) => this.initQualityCheckItemPhaseForm(null, phase.id)) ?? [];

    if (missingPhases.length) {
      forms.push(...missingPhases);
    }

    return forms;
  }

  private static initQualityCheckItemPhaseForm(
    qualityCheckItemPhase: QualityCheckItemPhase | undefined,
    phaseId?: string
  ): FormGroup<FormGroupOf<QualityCheckItemPhase>> {
    const form = new FormGroup<FormGroupOf<QualityCheckItemPhase>>({
      id: new FormControl(qualityCheckItemPhase?.id),
      phaseId: new FormControl(qualityCheckItemPhase?.phaseId ?? phaseId, Validators.required),
      checked: new FormControl(!!qualityCheckItemPhase),
    });
    return form;
  }

  public static removeProducts(
    projectForm: FormGroup<FormGroupOf<Project>>,
    activityId: string | undefined = undefined,
    phaseId: string | undefined = undefined,
    constructionAreaId: string | undefined = undefined
  ) {
    const productFormArray = projectForm.controls.products as FormArray;
    const productFormArrayLength = productFormArray.length;

    productFormArray.controls
      .slice()
      .reverse()
      .forEach((productForm, ip) => {
        if (productForm.value.activityId === activityId || productForm.value.phaseId === phaseId) {
          productFormArray.removeAt(productFormArrayLength - 1 - ip);
        } else {
          const quantityPerConstructionAreaFormArray = (productForm as FormGroup).controls[
            'quantityPerConstructionAreas'
          ] as FormArray;
          const quantityPerConstructionAreaFormArrayLength = quantityPerConstructionAreaFormArray.length;

          quantityPerConstructionAreaFormArray.controls
            .slice()
            .reverse()
            .forEach((quantityPerConstructionAreaForm, iq) => {
              if (quantityPerConstructionAreaForm.value.constructionAreaId === constructionAreaId) {
                quantityPerConstructionAreaFormArray.removeAt(quantityPerConstructionAreaFormArrayLength - 1 - iq);
              }
            });
        }
      });
  }

  public static initProductTemplateForm(
    productTemplate?: ProductTemplate,
    projectId?: string
  ): FormGroup<FormGroupOf<ProductTemplate>> {
    const form = new FormGroup<FormGroupOf<ProductTemplate>>({
      id: new FormControl(productTemplate?.id),
      projectId: new FormControl(productTemplate?.projectId ?? projectId),
      sequence: new FormControl(productTemplate?.sequence),
      provider: new FormControl(productTemplate?.provider ?? ProductTemplateProviderType.None),
      name: new FormControl(productTemplate?.name, Validators.required),
      referenceCode: new FormControl(productTemplate?.referenceCode),
      type: new FormControl(productTemplate?.type),
      skillsRequired: new FormControl(productTemplate?.skillsRequired),
      specialTools: new FormControl(productTemplate?.specialTools),
      supplier: new FormControl(productTemplate?.supplier),
      supplierCode: new FormControl(productTemplate?.supplierCode),
      unionCode: new FormControl(productTemplate?.unionCode),
      unionName: new FormControl(productTemplate?.unionName),
      unitCost: new FormControl(productTemplate?.unitCost),
      notes: new FormControl(productTemplate?.notes),
      unitOfMeasure: new FormControl(productTemplate?.unitOfMeasure),
      additionalCostsPercentage: new FormControl(productTemplate?.additionalCostsPercentage ?? 0, [Validators.min(0)]),
      additionalSubcontractorCosts: new FormControl(productTemplate?.additionalSubcontractorCosts),
      specialTreatmentCosts: new FormControl(productTemplate?.specialTreatmentCosts),
      additionalLabourCostsPercentage: new FormControl(productTemplate?.additionalLabourCostsPercentage ?? 0, [
        Validators.min(0),
      ]),
      quantityDocumentation: new FormControl(productTemplate?.quantityDocumentation),

      labourCostMethod: new FormControl(
        productTemplate?.labourCostMethod ?? LabourCostMethod.InstallationTime,
        Validators.required
      ),
      baseSalaryPerUnit: new FormControl(productTemplate?.baseSalaryPerUnit),
      baseUnitTime: new FormControl(productTemplate?.baseUnitTime ?? 0),

      reminders: new FormArray<FormGroup<FormGroupOf<ProductTemplateReminder>>>(
        productTemplate?.reminders?.map((reminder) => this.initProductTemplateReminderForm(reminder)) ?? []
      ),
    });

    const updateLabourCostsControl = (method: LabourCostMethod) => {
      updateLabourCostsMethodControls(form.controls, form.enabled, method);
    };

    form.controls.labourCostMethod.valueChanges.subscribe((method) => {
      updateLabourCostsControl(method);
    });

    const originalEnable = form.enable.bind(form);
    form.enable = () => {
      originalEnable();
      updateLabourCostsControl(form.controls.labourCostMethod.value);
    };

    if (productTemplate) {
      form.disable();
    }

    return form;
  }

  public static initProductTemplateReminderForm(
    reminder?: ProductTemplateReminder
  ): FormGroup<FormGroupOf<ProductTemplateReminder>> {
    const form = new FormGroup<FormGroupOf<ProductTemplateReminder>>({
      id: new FormControl(reminder?.id ?? generateGuid()),
      name: new FormControl(reminder?.name ?? null, Validators.required),
      weeksOrderPeriod: new FormControl(reminder?.weeksOrderPeriod ?? null, Validators.required),
      description: new FormControl(reminder?.description ?? null, Validators.required),
    });
    return form;
  }

  public static initProductPackageSummaryForm(
    summary: ProductPackageSummary
  ): FormGroup<FormGroupOf<ProductPackageSummary>> {
    return new FormGroup<FormGroupOf<ProductPackageSummary>>({
      id: new FormControl(summary.id),
      code: new FormControl(summary.code),
      name: new FormControl(summary.name),
      itemCount: new FormControl(summary.itemCount),
      productsSummary: new FormControl(summary.productsSummary),
    });
  }

  public static initProductPackageForm(productPackage?: ProductPackage): FormGroup<FormGroupOf<ProductPackage>> {
    const form = new FormGroup<FormGroupOf<ProductPackage>>({
      id: new FormControl(productPackage?.id),
      projectId: new FormControl(productPackage?.projectId),
      code: new FormControl(productPackage?.code, Validators.required),
      name: new FormControl(productPackage?.name, Validators.required),
      unitOfMeasure: new FormControl(productPackage?.unitOfMeasure),
      quantity: new FormControl(productPackage?.quantity),
      items: new FormArray<FormGroup<FormGroupOf<ProductPackageItem>>>(
        productPackage?.items?.map((item) => this.initProductPackageItemForm(item)) ?? []
      ),
      linkedPackageIds: new FormControl(productPackage?.linkedPackageIds ?? []),
      itemCount: new FormControl(productPackage?.itemCount),
      productsSummary: new FormControl(productPackage?.productsSummary),
      linkedPackages: new FormArray<FormGroup<FormGroupOf<ProductPackageSummary>>>(
        productPackage?.linkedPackages?.map((linkedPackage) => this.initProductPackageSummaryForm(linkedPackage)) ?? []
      ),
    });

    if (productPackage?.id) {
      form.disable();
    }

    return form;
  }

  public static initProductPackageItemForm(
    packageItem?: ProductPackageItem,
    productTemplate?: ProductTemplate
  ): FormGroup<FormGroupOf<ProductPackageItem>> {
    const form = new FormGroup<FormGroupOf<ProductPackageItem>>({
      id: new FormControl(packageItem?.id ?? generateGuid()),
      productTemplateId: new FormControl(packageItem?.productTemplateId ?? productTemplate?.id),
      referenceCode: new FormControl(packageItem?.referenceCode ?? productTemplate?.referenceCode),
      name: new FormControl(packageItem?.name ?? productTemplate?.name),
      quantity: new FormControl(packageItem?.quantity ?? 1, [Validators.required, Validators.min(1)]),
      phaseTemplateId: new FormControl(packageItem?.phaseTemplateId),
      specialTreatmentCosts: new FormControl(packageItem?.specialTreatmentCosts),
      labourCostMethod: new FormControl(
        productTemplate ? undefined : (packageItem?.labourCostMethod ?? LabourCostMethod.UnionPrice)
      ),
      baseSalaryPerUnit: new FormControl(packageItem?.baseSalaryPerUnit),
      baseUnitTime: new FormControl(packageItem?.baseUnitTime),
      unitCost: new FormControl(packageItem?.unitCost),
      additionalCostsPercentage: new FormControl(packageItem?.additionalCostsPercentage),
      additionalSubcontractorCosts: new FormControl(packageItem?.additionalSubcontractorCosts),
      additionalLabourCostsPercentage: new FormControl(packageItem?.additionalLabourCostsPercentage),
    });

    const templateOverrideControls = getProductPackageItemProductControls(form.controls);

    const updateTemplateOverrideControls = (productTemplateId: string) => {
      const canEditProductFields = form.enabled && !productTemplateId;

      if (canEditProductFields) {
        templateOverrideControls.forEach((control) => control.enable({ onlySelf: true, emitEvent: false }));
      } else {
        if (productTemplateId) {
          templateOverrideControls.forEach((control) => control.setValue(null, { onlySelf: true, emitEvent: false }));
        }

        templateOverrideControls.forEach((control) => control.disable({ onlySelf: true, emitEvent: false }));
      }

      updateLabourCostsMethodControls(form.controls, canEditProductFields, form.controls.labourCostMethod.value);
    };

    form.controls.productTemplateId.valueChanges.subscribe((productTemplateId) => {
      updateTemplateOverrideControls(productTemplateId);
    });

    form.controls.labourCostMethod.valueChanges.subscribe((method) => {
      updateLabourCostsMethodControls(form.controls, form.enabled && !form.controls.productTemplateId.value, method);
    });

    updateTemplateOverrideControls(form.controls.productTemplateId.value);

    const originalEnable = form.enable.bind(form);
    form.enable = () => {
      originalEnable();
      updateTemplateOverrideControls(form.controls.productTemplateId.value);
    };

    return form;
  }

  public static initProductPackageEditItemForm(
    row: ProductPackageEditItem
  ): FormGroup<FormGroupOf<ProductPackageEditItem>> {
    const form = new FormGroup<FormGroupOf<ProductPackageEditItem>>({
      productId: new FormControl(row.productId),
      item: new FormGroup<FormGroupOf<ProductPackageItem>>({
        id: new FormControl(row.item.id),
        productTemplateId: new FormControl(row.item.productTemplateId),
        referenceCode: new FormControl(row.item.referenceCode),
        name: new FormControl(row.item.name),
        quantity: new FormControl(row.item.quantity),
        phaseTemplateId: new FormControl(row.item.phaseTemplateId),
        specialTreatmentCosts: new FormControl(row.item.specialTreatmentCosts),
        labourCostMethod: new FormControl(row.item.labourCostMethod),
        baseSalaryPerUnit: new FormControl(row.item.baseSalaryPerUnit),
        baseUnitTime: new FormControl(row.item.baseUnitTime),
        unitCost: new FormControl(row.item.unitCost),
        additionalCostsPercentage: new FormControl(row.item.additionalCostsPercentage),
        additionalSubcontractorCosts: new FormControl(row.item.additionalSubcontractorCosts),
        additionalLabourCostsPercentage: new FormControl(row.item.additionalLabourCostsPercentage),
      }),
      phaseId: new FormControl(row.phaseId, Validators.required),
      quantity: new FormControl(row.quantity, [Validators.required, Validators.min(0)]),
      sourcePackageId: new FormControl(row.sourcePackageId),
      sourcePackageGuid: new FormControl(row.sourcePackageGuid),
      sourcePackageName: new FormControl(row.sourcePackageName),
      sourcePackageCode: new FormControl(row.sourcePackageCode),
      sourcePackageItemId: new FormControl(row.sourcePackageItemId),
    });

    const hasProductTemplate = !!row.item.productTemplateId;
    if (hasProductTemplate) {
      getProductPackageItemProductControls(form.controls.item.controls).forEach((control) =>
        control.disable({ onlySelf: true, emitEvent: false })
      );
    }

    updateLabourCostsMethodControls(
      form.controls.item.controls,
      !hasProductTemplate,
      form.controls.item.controls.labourCostMethod.value
    );
    form.controls.item.controls.labourCostMethod.valueChanges.subscribe((method) => {
      updateLabourCostsMethodControls(form.controls.item.controls, !hasProductTemplate, method);
    });

    return form;
  }

  public static initProductPackagesMassEditRowForm(
    row: ProductPackagesMassEditRow
  ): FormGroup<FormGroupOf<ProductPackagesMassEditRow>> {
    return new FormGroup<FormGroupOf<ProductPackagesMassEditRow>>({
      id: new FormControl(row.id),
      packageId: new FormControl(row.packageId),
      packageName: new FormControl(row.packageName),
      packageCode: new FormControl(row.packageCode),
      sourcePackageName: new FormControl(row.sourcePackageName),
      sourcePackageCode: new FormControl(row.sourcePackageCode),
      referenceCode: new FormControl(row.referenceCode),
      name: new FormControl(row.name),
      quantity: new FormControl(row.quantity, [Validators.required, Validators.min(0)]),
      phaseId: new FormControl(row.phaseId, Validators.required),
    });
  }

  public static initQualityCheckItemTemplateForm(
    item: QualityCheckItemTemplate | undefined,
    languageIds: string[]
  ): FormGroup<FormGroupOf<QualityCheckItemTemplate>> {
    return new FormGroup<FormGroupOf<QualityCheckItemTemplate>>({
      id: new FormControl(item?.id),
      companyId: new FormControl(item?.companyId),
      order: new FormControl(item?.order ?? 0),
      name: new FormControl(item?.name),
      nameTranslation: FormGenerator.initTranslationDictionaryForm(item?.nameTranslation, languageIds),
      code: new FormControl(item?.code, Validators.required),
      created: new FormControl(item?.created),
      comment: new FormControl(item?.comment),
      commentTranslation: FormGenerator.initTranslationDictionaryForm(item?.commentTranslation, languageIds),
      reference: new FormControl(item?.reference),
      referenceTranslation: FormGenerator.initTranslationDictionaryForm(item?.referenceTranslation, languageIds),
    });
  }

  public static initQualityCheckListTemplateForm(
    item: QualityCheckListTemplate | undefined,
    languageIds: string[]
  ): FormGroup<FormGroupOf<QualityCheckListTemplate>> {
    return new FormGroup<FormGroupOf<QualityCheckListTemplate>>({
      id: new FormControl(item?.id),
      companyId: new FormControl(item?.companyId),
      order: new FormControl(item?.order ?? 0),
      name: FormGenerator.initTranslationDictionaryForm(item?.name, languageIds),
      items: new FormArray(
        item?.items?.map((templateItem) => FormGenerator.initQualityCheckListTemplateItemForm(templateItem)) ?? []
      ),
      itemCount: new FormControl(item?.itemCount ?? 0),
      itemsSummary: new FormControl(item?.itemsSummary),
      created: new FormControl(item?.created),
    });
  }

  public static initQualityCheckListTemplateItemForm(
    item: QualityCheckListTemplateItem | undefined,
    qualityCheckItemTemplateId?: string,
    order?: number
  ): FormGroup<FormGroupOf<QualityCheckListTemplateItem>> {
    return new FormGroup<FormGroupOf<QualityCheckListTemplateItem>>({
      id: new FormControl(item?.id ?? generateGuid()),
      qualityCheckListTemplateId: new FormControl(item?.qualityCheckListTemplateId),
      qualityCheckItemTemplateId: new FormControl(
        item?.qualityCheckItemTemplateId ?? qualityCheckItemTemplateId,
        Validators.required
      ),
      order: new FormControl(item?.order ?? order ?? 0),
    });
  }

  public static initWorkUnitInTimeDataForm(
    item?: WorkUnitInTimeData,
    project?: Project
  ): FormGroup<FormGroupOf<WorkUnitInTimeData>> {
    const form = new FormGroup<FormGroupOf<WorkUnitInTimeData>>({
      day: new FormControl(item?.day),
      workUnitId: new FormControl(item?.workUnit.id),
      workUnit: FormGenerator.initWorkUnitForm(item?.workUnit, project),
      workUnitInTimeId: new FormControl(item?.workUnitInTimeId),
      progress: new FormControl(item?.progress),
    });

    form.disable();

    return form;
  }

  public static initWorkUnitForm(item: WorkUnit, project: Project): FormGroup<FormGroupOf<WorkUnit>> {
    const form = new FormGroup<FormGroupOf<WorkUnit>>({
      id: new FormControl(item?.id),
      type: new FormControl(item?.type),
      constructionAreaId: new FormControl(item?.constructionAreaId),
      productId: new FormControl(item?.productId),
      activityId: new FormControl(item?.activityId),
      phaseId: new FormControl(item?.phaseId),
      subPhaseId: new FormControl(item?.subPhaseId),
      subCompanyIds: new FormControl(item?.subCompanyIds ?? []),
      qualityCheckPictures: new FormArray(
        item?.qualityCheckPictures?.map((x) => FormGenerator.initQualityCheckPictureForm(x)) ?? []
      ),
      qualityCheckLists: new FormArray(
        item?.qualityCheckLists?.map((qualityCheckList) =>
          FormGenerator.initQualityCheckListForm(qualityCheckList, project, undefined, undefined)
        ) ?? []
      ),
      files: new FormArray(item?.files?.map((x) => FormGenerator.initWorkUnitFileForm(x)) ?? []),
      isDone: new FormControl(item?.isDone ?? false),
      isLinkedToMasterWorkUnit: new FormControl(item?.isLinkedToMasterWorkUnit ?? false),
    });
    return form;
  }

  public static initQualityCheckPictureForm(
    item?: QualityCheckPicture,
    image?: DocumentFile,
    planLocation?: LocationOnPlan
  ): FormGroup<FormGroupOf<QualityCheckPicture>> {
    const form = new FormGroup<FormGroupOf<QualityCheckPicture>>({
      id: new FormControl(item?.id),
      created: new FormControl(item?.created),
      createdById: new FormControl(item?.createdById),
      workUnitId: new FormControl(item?.workUnitId),
      notes: new FormControl(item?.notes),
      imageId: new FormControl(item?.imageId ?? image?.id),
      image: this.initFileForm(item?.image ?? image),
      locationOnPlanId: new FormControl(item?.locationOnPlanId),
      locationOnPlan: this.initFileForm(item?.locationOnPlan),
      planLocationId: new FormControl(item?.planLocationId ?? planLocation?.id),
      planLocation: this.initLocationOnPlanForm(item?.planLocation ?? planLocation),
    });

    return form;
  }

  public static initQualityCheckListForm(
    item: WorkUnitQualityCheckList | undefined,
    project: Project,
    languageIds: string[],
    planLocation?: LocationOnPlan
  ): FormGroup<FormGroupOf<WorkUnitQualityCheckList>> {
    const form = new FormGroup<FormGroupOf<WorkUnitQualityCheckList>>({
      id: new FormControl(item?.id),
      createdById: new FormControl(item?.createdById),
      workUnitId: new FormControl(item?.workUnitId),
      qualityCheckListTemplateId: new FormControl(item?.qualityCheckListTemplateId),
      name: new FormControl(item?.name),
      created: new FormControl(item?.created),
      locationOnPlanId: new FormControl(item?.locationOnPlanId),
      locationOnPlan: this.initFileForm(item?.locationOnPlan),
      planLocationId: new FormControl(item?.planLocationId ?? planLocation?.id),
      planLocation: this.initLocationOnPlanForm(item?.planLocation ?? planLocation),
      doneQualityCheckItems: new FormArray(
        item?.doneQualityCheckItems?.map((doneQualityCheckItem) =>
          FormGenerator.initWorkUnitDoneQualityCheckItemForm(
            project,
            doneQualityCheckItem,
            undefined,
            languageIds,
            null
          )
        ) ?? []
      ),
    });
    return form;
  }

  public static initLocationOnPlanForm(item?: LocationOnPlan): FormGroup<FormGroupOf<LocationOnPlan>> {
    const form = new FormGroup<FormGroupOf<LocationOnPlan>>({
      id: new FormControl(item?.id),
      locationX: new FormControl(item?.locationX),
      locationY: new FormControl(item?.locationY),
      planId: new FormControl(item?.planId),
      plan: this.initPlanForm(item?.plan),
    });
    return form;
  }

  public static initWorkUnitFileForm(
    item?: WorkUnitFile,
    documentFile?: DocumentFile
  ): FormGroup<FormGroupOf<WorkUnitFile>> {
    const form = new FormGroup<FormGroupOf<WorkUnitFile>>({
      id: new FormControl(item?.id),
      workUnitId: new FormControl(item?.workUnitId),
      created: new FormControl(item?.created ? toDate(item.created) : toDate()),
      createdById: new FormControl(item?.createdById),
      notes: new FormControl(item?.notes),
      documentFileId: new FormControl(item?.documentFileId ?? documentFile?.id),
      documentFile: this.initFileForm(item?.documentFile ?? documentFile),
    });
    return form;
  }

  public static initWorkUnitDoneQualityCheckItemForm(
    project: Project | undefined,
    doneQualityCheckItem: WorkUnitDoneQualityCheckItem | undefined,
    qualityCheckItem: QualityCheckItem | undefined,
    languageIds: string[],
    checkedByUserId: string,
    qualityCheckItemTemplateId?: string
  ): FormGroup<FormGroupOf<WorkUnitDoneQualityCheckItem>> {
    const form = new FormGroup<FormGroupOf<WorkUnitDoneQualityCheckItem>>({
      id: new FormControl(doneQualityCheckItem?.id ?? generateGuid()),
      created: new FormControl(doneQualityCheckItem?.created ? toDate(doneQualityCheckItem.created) : toDate()),
      checkedByUserId: new FormControl(doneQualityCheckItem?.checkedByUserId ?? checkedByUserId),
      qualityCheckItemId: new FormControl(doneQualityCheckItem?.qualityCheckItemId ?? qualityCheckItem?.id),
      qualityCheckItemTemplateId: new FormControl(
        doneQualityCheckItem?.qualityCheckItemTemplateId ?? qualityCheckItemTemplateId
      ),
      qualityCheckItem: this.initQualityCheckItemForm(
        project,
        doneQualityCheckItem?.qualityCheckItem ?? qualityCheckItem,
        undefined,
        undefined,
        languageIds
      ),
      status: new FormControl(doneQualityCheckItem?.status ?? WorkUnitDoneQualityCheckItemStatus.NotApproved),
      comment: new FormControl(doneQualityCheckItem?.comment),
      notes: new FormControl(doneQualityCheckItem?.notes),
      notApplicable: new FormControl(doneQualityCheckItem?.notApplicable ?? false),
    });
    return form;
  }

  public static initWorkUnitInTimeForm(
    entry?: WorkUnitInTime,
    day?: string,
    workUnitId?: string,
    previousWeekProgress?: number
  ): FormGroup<FormGroupOf<WorkUnitInTime>> {
    const form = new FormGroup<FormGroupOf<WorkUnitInTime>>({
      id: new FormControl(entry?.id ?? generateGuid()),
      day: new FormControl(entry?.day ?? day, Validators.required),
      workUnitId: new FormControl(entry?.workUnitId ?? workUnitId),
      projectId: new FormControl(entry?.projectId),
      employeesCountWorkingPerWeekPlanned: new FormControl(entry?.employeesCountWorkingPerWeekPlanned ?? 0),
      correctionHoursUsed: new FormControl(entry?.correctionHoursUsed ?? 0, Validators.required),
      correctionRemainingHours: new FormControl(entry?.correctionRemainingHours ?? 0, Validators.required),
      estimatedUsedMaterialCorrection: new FormControl(
        entry?.estimatedUsedMaterialCorrection ?? 0,
        Validators.required
      ),
      correctionRemainingMaterialCost: new FormControl(
        entry?.correctionRemainingMaterialCost ?? 0,
        Validators.required
      ),
      forceLowerProgress: new FormControl(entry?.forceLowerProgress ?? false),
      assignedEmployees: new FormArray(
        entry?.assignedEmployees?.map((x) => FormGenerator.initAssignedEmployeeForm(x)) ?? []
      ),
      progress: new FormControl(entry?.progress ?? previousWeekProgress ?? 0, Validators.required),
      previousWeekProgress: new FormControl(entry?.previousWeekProgress ?? previousWeekProgress ?? 0),
      plannedProgress: new FormControl(entry?.plannedProgress ?? 0),
      plannedHours: new FormControl(entry?.plannedHours ?? 0),
      enteredEmployeeHours: new FormControl(entry?.enteredEmployeeHours ?? 0),
      combinedHoursUsed: new FormControl(entry?.combinedHoursUsed ?? 0),
    });

    return form;
  }

  public static initStaffingUnitForm(
    entry?: StaffingUnit,
    type: StaffingUnitType = StaffingUnitType.Standalone,
    fromWeek?: string,
    toWeek?: string
  ): FormGroup<FormGroupOf<StaffingUnit>> {
    const form = new FormGroup<FormGroupOf<StaffingUnit>>({
      id: new FormControl(entry?.id ?? generateGuid()),
      name: new FormControl(
        entry?.name,
        entry?.type == StaffingUnitType.Standalone || (!entry && type == StaffingUnitType.Standalone)
          ? [Validators.required]
          : []
      ),
      type: new FormControl(entry?.type !== undefined ? entry.type : type),
      staffingUnitsInTime: new FormArray(
        entry?.staffingUnitsInTime
          ?.filter((x) => !fromWeek || fromDayString(x.day) >= fromDayString(fromWeek))
          .filter((x) => !toWeek || fromDayString(x.day) <= fromDayString(toWeek))
          .map((x) => FormGenerator.initStaffingUnitInTimeForm(x)) ?? []
      ),
    });

    return form;
  }

  public static initStaffingUnitInTimeForm(
    entry?: StaffingUnitInTime,
    day?: string,
    staffingUnitId?: string
  ): FormGroup<FormGroupOf<StaffingUnitInTime>> {
    const form = new FormGroup<FormGroupOf<StaffingUnitInTime>>({
      id: new FormControl(entry?.id ?? generateGuid()),
      day: new FormControl(entry?.day ?? day, Validators.required),
      staffingUnitId: new FormControl(entry?.staffingUnitId ?? staffingUnitId),
      assignedEmployees: new FormArray(
        entry?.assignedEmployees?.map((x) => FormGenerator.initAssignedEmployeeForm(x)) ?? []
      ),
      employeesCountWorkingPerWeekPlanned: new FormControl(entry?.employeesCountWorkingPerWeekPlanned || 0),
    });

    return form;
  }

  public static initProjectMilestoneForm(
    milestone: ProjectMilestone | undefined = undefined,
    languageIds: string[] | undefined = undefined,
    color: string | undefined = undefined
  ): FormGroup<FormGroupOf<ProjectMilestone>> {
    return new FormGroup<FormGroupOf<ProjectMilestone>>({
      id: new FormControl(milestone?.id),
      name: FormGenerator.initTranslationDictionaryForm(milestone?.name, languageIds, Validators.required),
      description: FormGenerator.initTranslationDictionaryForm(milestone?.description, languageIds),
      color: new FormControl(milestone?.color ?? color),
      date: new FormControl(milestone?.date),
      imageId: new FormControl(milestone?.imageId ?? milestone?.image?.id),
      image: FormGenerator.initFileForm(milestone?.image),
    });
  }

  public static initAssignedEmployeeForm(
    data?: AssignedEmployee,
    userId?: string,
    days?: DayOfWeek[],
    startsAt?: string,
    endsAt?: string
  ): FormGroup<FormGroupOf<AssignedEmployee>> {
    const form = new FormGroup<FormGroupOf<AssignedEmployee>>({
      id: new FormControl(data?.id ?? generateGuid()),
      userId: new FormControl(data?.userId ?? userId, Validators.required),
      days: new FormControl(
        data?.days ??
          days ?? [DayOfWeek.Monday, DayOfWeek.Tuesday, DayOfWeek.Wednesday, DayOfWeek.Thursday, DayOfWeek.Friday],
        [Validators.required]
      ),
      startsAt: new FormControl(data?.startsAt ?? startsAt),
      endsAt: new FormControl(data?.endsAt ?? endsAt),
      staffingUnitInTimeId: new FormControl(data?.staffingUnitInTimeId),
      workUnitInTimeId: new FormControl(data?.workUnitInTimeId),
    });

    return form;
  }

  public static initServiceUnitForm(entry?: ServiceUnit, week?: string): FormGroup<FormGroupOf<ServiceUnit>> {
    const form = new FormGroup<FormGroupOf<ServiceUnit>>({
      id: new FormControl(entry?.id ?? generateGuid()),
      projectId: new FormControl(entry?.projectId),
      serviceUnitsInTime: new FormArray(
        entry?.serviceUnitsInTime
          ?.filter((x) => !week || isEqual(x.week, week))
          .flatMap((x) =>
            EnumExtension.getValues(DayOfWeek).map((day) => FormGenerator.initServiceUnitInTimeForm(x, week, day))
          ) ??
          (week
            ? EnumExtension.getValues(DayOfWeek).map((day) => FormGenerator.initServiceUnitInTimeForm(null, week, day))
            : [])
      ),
    });

    return form;
  }

  public static initServiceUnitInTimeForm(
    entry?: ServiceUnitInTime,
    week?: string,
    day?: DayOfWeek,
    users?: { id: string; startsAt: string; endsAt: string }[]
  ): FormGroup<FormGroupOf<ServiceUnitInTime>> {
    const form = new FormGroup<FormGroupOf<ServiceUnitInTime>>({
      id: new FormControl(entry?.id ?? generateGuid()),
      day: new FormControl(entry?.day ?? day, Validators.required),
      week: new FormControl(entry?.week ?? week, Validators.required),
      assignedEmployees: new FormArray(
        entry?.assignedEmployees
          ?.filter((x) => !day || x.days.includes(day))
          .map((x) => FormGenerator.initAssignedEmployeeForm(x)) ??
          users?.map((x) => FormGenerator.initAssignedEmployeeForm(null, x.id, [day], x.startsAt, x.endsAt)) ??
          []
      ),
    });

    return form;
  }

  public static initHoursInWeekForOneEmployeePlannedForm(
    data: HoursInWeekForOneEmployeePlanned | undefined,
    day?: string,
    value?: number
  ): FormGroup<FormGroupOf<HoursInWeekForOneEmployeePlanned>> {
    const form = new FormGroup({
      id: new FormControl(data?.id ?? generateGuid()),
      day: new FormControl(day ?? data?.day, Validators.required),
      value: new FormControl(value ?? data?.value, Validators.required),
    });
    return form;
  }

  public static initTimeEntryCategoryForm(
    timeEntryCategory: TimeEntryCategory | undefined,
    languageIds: string[],
    type?: TimeEntryType,
    order?: number
  ): FormGroup<FormGroupOf<TimeEntryCategory>> {
    const form = new FormGroup<FormGroupOf<TimeEntryCategory>>({
      id: new FormControl(timeEntryCategory?.id ?? generateGuid()),
      name: new FormControl(timeEntryCategory?.name),
      nameTranslation: FormGenerator.initTranslationDictionaryForm(timeEntryCategory?.nameTranslation, languageIds),
      wageType: new FormControl(timeEntryCategory?.wageType, Validators.required),
      order: new FormControl(timeEntryCategory?.order ?? order ?? 1, Validators.required),
      factor: new FormControl(timeEntryCategory?.factor ?? 1, Validators.required),
      type: new FormControl(timeEntryCategory?.type ?? type, Validators.required),
      dataType: new FormControl(timeEntryCategory?.dataType ?? TimeEntryDataType.Time, Validators.required),
      isTaxDeduced: new FormControl(timeEntryCategory?.isTaxDeduced ?? false),
      editableInMobileApp: new FormControl(timeEntryCategory?.editableInMobileApp ?? true),
    });

    if (timeEntryCategory) {
      form.disable();
    }

    return form;
  }

  public static initEmployeeHours(
    item?: EmployeeHours,
    projectId?: string,
    day?: string,
    userId?: string,
    timeEntryCategoryId?: string,
    disable = false
  ): FormGroup<FormGroupOf<EmployeeHours>> {
    const form = new FormGroup<FormGroupOf<EmployeeHours>>({
      guid: new FormControl(generateGuid()),
      id: new FormControl(item?.id),
      created: new FormControl(item?.created ? toDate(item.created) : toDate()),
      userId: new FormControl(item?.userId ?? userId, Validators.required),
      day: new FormControl(item?.day ?? day, Validators.required),
      timeEntryCategoryId: new FormControl(item?.timeEntryCategoryId ?? timeEntryCategoryId),
      startsAt: new FormControl(
        item?.startsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      endsAt: new FormControl(
        item?.endsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      includePauseInTotalHours: new FormControl(item?.includePauseInTotalHours ?? false),
      pauseStartsAt: new FormControl(
        item?.pauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      pauseEndsAt: new FormControl(
        item?.pauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      distance: new FormControl(item?.distance),
      price: new FormControl(item?.price),
      notes: new FormControl(item?.notes),
      workUnitId: new FormControl(item?.workUnitId),
      workUnitInTimeId: new FormControl(item?.workUnitInTimeId),
      projectId: new FormControl(item?.projectId ?? projectId, Validators.required),
      status: new FormControl(item?.status ?? EmployeeHoursStatus.NotApproved),
      files: new FormArray(item?.files?.map((x) => FormGenerator.initFileForm(x)) ?? []),
    });

    if (item || disable) {
      form.disable();
    }

    return form;
  }

  public static initResetEmailForm(data?: ResetEmailRequest): FormGroup<FormGroupOf<ResetEmailRequest>> {
    const form = new FormGroup<FormGroupOf<ResetEmailRequest>>({
      oldEmail: new FormControl(data?.oldEmail, [Validators.required, Validators.email, Validators.minLength(5)]),
      email: new FormControl(data?.email, [Validators.required, Validators.email, Validators.minLength(5)]),
      password: new FormControl(data?.password, [Validators.required, Validators.minLength(5)]),
      token: new FormControl(data?.token, [Validators.required, Validators.minLength(3)]),
    });
    return form;
  }

  public static initActivateAccountForm(
    translate: TranslateService,
    data?: ActivateAccountRequest
  ): FormGroup<FormGroupOf<ActivateAccountRequest>> {
    const form = new FormGroup<FormGroupOf<ActivateAccountRequest>>({
      email: new FormControl({ value: data?.email, disabled: true }, [
        Validators.required,
        Validators.email,
        Validators.minLength(5),
      ]),
      token: new FormControl(data?.token, [Validators.required, Validators.minLength(3)]),
      password: new FormControl(data?.password, [Validators.required, Validators.minLength(5)]),
      confirmPassword: new FormControl(null, [
        Validators.required,
        FormGroupValidators.requiredSameValueAsValidator(
          translate,
          'password',
          'auth.activateAccount.confirmPassword',
          'auth.activateAccount.password'
        ),
      ]),
    });
    return form;
  }

  public static initItemsForm<T>(items: FormGroup<FormGroupOf<T>>[]): FormGroup<FormArrayOf<T>> {
    const form = new FormGroup({ items: new FormArray(items) });
    form.disable();
    return form;
  }

  public static initPhaseLinkForm(value?: PhaseLink & { durationDays?: number }) {
    return new FormGroup({
      constructionAreaId: new FormControl(value?.constructionAreaId),
      phaseId: new FormControl(value?.phaseId),
      startDate: new FormControl(value?.startDate),
      endDate: new FormControl(value?.endDate),
      durationDays: new FormControl(value?.durationDays),
      linkedPhaseIds: new FormControl(value?.linkedPhaseIds ?? []),
      previousPhaseIds: new FormControl(value?.previousPhaseIds ?? []),
      propagationAreaIds: new FormControl<string[]>([]),
    });
  }

  public static initForgotPasswordForm(data?: ForgotPasswordRequest): FormGroup<FormGroupOf<ForgotPasswordRequest>> {
    const form = new FormGroup<FormGroupOf<ForgotPasswordRequest>>({
      email: new FormControl(data?.email, [Validators.required, Validators.email, Validators.minLength(5)]),
    });
    return form;
  }

  public static initLoginForm(email?: string): FormGroup<FormGroupOf<LoginEmailRequest>> {
    const form = new FormGroup<FormGroupOf<LoginEmailRequest>>({
      email: new FormControl(email, [Validators.required]),
      password: new FormControl(null, [Validators.required]),
      twoFactorCode: new FormControl(null),
    });
    return form;
  }

  public static initResetPasswordForm(
    translate: TranslateService,
    data?: ResetPasswordRequest
  ): FormGroup<FormGroupOf<ResetPasswordRequest>> {
    const form = new FormGroup<FormGroupOf<ResetPasswordRequest>>({
      email: new FormControl(data?.email, [Validators.required, Validators.email, Validators.minLength(5)]),
      token: new FormControl(data?.token, [Validators.required, Validators.minLength(3)]),
      password: new FormControl(data?.password, [Validators.required, Validators.minLength(5)]),
      confirmPassword: new FormControl(null, [
        Validators.required,
        FormGroupValidators.requiredSameValueAsValidator(
          translate,
          'password',
          'auth.forgotPassword.confirmPassword',
          'auth.forgotPassword.newPassword'
        ),
      ]),
    });
    return form;
  }

  public static initChangePasswordForm(
    translate: TranslateService,
    data?: ChangePasswordRequest
  ): FormGroup<FormGroupOf<ChangePasswordRequest>> {
    const form = new FormGroup<FormGroupOf<ChangePasswordRequest>>({
      oldPassword: new FormControl(data?.oldPassword, Validators.required),
      password: new FormControl(data?.password, [
        Validators.required,
        Validators.minLength(5),
        FormGroupValidators.requiredNotSameValueAsValidator(
          translate,
          'oldPassword',
          'settings.account.oldPassword',
          'settings.account.password'
        ),
      ]),
      confirmPassword: new FormControl(null, [
        Validators.required,
        FormGroupValidators.requiredSameValueAsValidator(
          translate,
          'password',
          'settings.account.confirmPassword',
          'settings.account.password'
        ),
      ]),
    });
    return form;
  }

  public static initAnnouncementForm(
    item: Announcement | undefined,
    languageIds: string[]
  ): FormGroup<FormGroupOf<Announcement>> {
    return new FormGroup<FormGroupOf<Announcement>>({
      id: new FormControl(item?.id),
      isRemoved: new FormControl(item?.isRemoved ?? false),
      title: FormGenerator.initTranslationDictionaryForm(item?.title, languageIds, [Validators.required]),
      content: FormGenerator.initTranslationDictionaryForm(item?.content, languageIds, [Validators.required]),
      type: new FormControl(item?.type ?? AnnouncementType.MessageBar, Validators.required),
      status: new FormControl(item?.status ?? AnnouncementStatus.Draft, Validators.required),
      from: new FormControl(item?.from ? toDate(item.from) : null),
      to: new FormControl(item?.to ? toDate(item.to) : null),
      version: new FormControl(item?.version ?? '', Validators.maxLength(100)),
      route: new FormControl(item?.route ?? '', Validators.maxLength(500)),
    });
  }

  public static initWorkTaskForm(
    item: WorkTask | undefined,
    projectId?: string
  ): FormGroup<FormGroupOf<WorkTaskFormModel>> {
    return new FormGroup<FormGroupOf<WorkTaskFormModel>>({
      id: new FormControl(item?.id),
      title: new FormControl(item?.title ?? '', [Validators.required, Validators.maxLength(500)]),
      description: new FormControl(item?.description ?? ''),
      status: new FormControl(item?.status ?? WorkTaskStatus.ToDo),
      priority: new FormControl(item?.priority ?? WorkTaskPriority.Medium, Validators.required),
      sortOrder: new FormControl(item?.sortOrder ?? 0),
      projectId: new FormControl(item?.projectId ?? projectId),
      workUnitId: new FormControl(item?.workUnitId),
      week: new FormControl(item?.week),
      assignedEmployeeIds: new FormControl(item?.assignedEmployees.map((user) => user.id) ?? []),
    });
  }

  public static initHelpForm(help: Help | undefined, languageIds: string[]): FormGroup<FormGroupOf<Help>> {
    const form = new FormGroup<FormGroupOf<Help>>({
      id: new FormControl(help?.id),
      key: new FormControl(help?.key),
      helpContent: FormGenerator.initTranslationDictionaryForm(help?.helpContent, languageIds, [Validators.required]),
      aiContext: new FormControl(help?.aiContext),
    });

    form.disable();

    return form;
  }

  public static initTranslationDictionaryForm(
    item: TranslationDictionary | undefined,
    languageIds: string[] | undefined | null,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null
  ): FormGroup<FormGroupOf<TranslationDictionary>> {
    item ??= {};
    languageIds ??= [];
    const languageKeys = Array.from(new Set([...languageIds, ...Object.keys(item)]));

    const form = new FormGroup<FormGroupOf<TranslationDictionary>>({
      ...languageKeys.reduce(
        (dic, lang) => {
          dic[lang] = new FormControl(item[lang], validatorOrOpts);
          return dic;
        },
        {} as { [key: string]: FormControl }
      ),
    });
    return form;
  }

  public static initCompanySubscriptionForm(
    sub: CompanySubscription | undefined = undefined,
    companyId: string | undefined = undefined,
    disable = true
  ): FormGroup<FormGroupOf<CompanySubscription>> {
    const form = new FormGroup<FormGroupOf<CompanySubscription>>({
      id: new FormControl(sub?.id),
      companyId: new FormControl(sub?.companyId ?? companyId),
      status: new FormControl(sub?.status ?? CompanySubscriptionStatus.Cancelled),
      trialEndsAt: new FormControl(sub?.trialEndsAt ? toDate(sub?.trialEndsAt) : null),
      subscriptionEndsAt: new FormControl(sub?.subscriptionEndsAt ? toDate(sub?.subscriptionEndsAt) : null),
      paymentConfigured: new FormControl(sub?.paymentConfigured ?? false),
      chatbotEnabled: new FormControl(sub?.chatbotEnabled ?? false),
      billingEmail: new FormControl(sub?.billingEmail),
    });

    if (sub && disable) {
      form.disable();
    }

    return form;
  }

  public static initWorkingHoursForm(
    workingHours: WorkingHours | undefined = undefined,
    projectId: string | undefined = undefined,
    day: string | undefined = undefined
  ): FormGroup<FormGroupOf<WorkingHours>> {
    const form = new FormGroup<FormGroupOf<WorkingHours>>({
      id: new FormControl(workingHours?.id),
      projectId: new FormControl(workingHours?.projectId ?? projectId),
      companyId: new FormControl(workingHours?.companyId),

      isDefault: new FormControl(workingHours?.isDefault ?? false),
      weekStartDay: new FormControl(workingHours?.weekStartDay ?? day),

      mondayStartsAt: new FormControl(
        workingHours?.mondayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      mondayEndsAt: new FormControl(
        workingHours?.mondayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      mondayPauseStartsAt: new FormControl(
        workingHours?.mondayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      mondayPauseEndsAt: new FormControl(
        workingHours?.mondayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      tuesdayStartsAt: new FormControl(
        workingHours?.tuesdayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      tuesdayEndsAt: new FormControl(
        workingHours?.tuesdayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      tuesdayPauseStartsAt: new FormControl(
        workingHours?.tuesdayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      tuesdayPauseEndsAt: new FormControl(
        workingHours?.tuesdayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      wednesdayStartsAt: new FormControl(
        workingHours?.wednesdayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      wednesdayEndsAt: new FormControl(
        workingHours?.wednesdayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      wednesdayPauseStartsAt: new FormControl(
        workingHours?.wednesdayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      wednesdayPauseEndsAt: new FormControl(
        workingHours?.wednesdayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      thursdayStartsAt: new FormControl(
        workingHours?.thursdayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      thursdayEndsAt: new FormControl(
        workingHours?.thursdayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      thursdayPauseStartsAt: new FormControl(
        workingHours?.thursdayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      thursdayPauseEndsAt: new FormControl(
        workingHours?.thursdayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      fridayStartsAt: new FormControl(
        workingHours?.fridayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      fridayEndsAt: new FormControl(
        workingHours?.fridayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      fridayPauseStartsAt: new FormControl(
        workingHours?.fridayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      fridayPauseEndsAt: new FormControl(
        workingHours?.fridayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      saturdayStartsAt: new FormControl(
        workingHours?.saturdayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      saturdayEndsAt: new FormControl(
        workingHours?.saturdayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      saturdayPauseStartsAt: new FormControl(
        workingHours?.saturdayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      saturdayPauseEndsAt: new FormControl(
        workingHours?.saturdayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),

      sundayStartsAt: new FormControl(
        workingHours?.sundayStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      sundayEndsAt: new FormControl(
        workingHours?.sundayEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      sundayPauseStartsAt: new FormControl(
        workingHours?.sundayPauseStartsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      sundayPauseEndsAt: new FormControl(
        workingHours?.sundayPauseEndsAt,
        FormGroupValidators.timeSpanValidator({ minHours: 0, maxHours: 23, minMinutes: 0, maxMinutes: 59 })
      ),
      workingHoursPerWeek: new FormControl(workingHours?.workingHoursPerWeek),
      netWorkingHoursPerWeek: new FormControl(workingHours?.netWorkingHoursPerWeek),
    });
    form.disable();

    return form;
  }

  public static initPlanForm(item: Plan | undefined): FormGroup<FormGroupOf<Plan>> {
    const form = new FormGroup<FormGroupOf<Plan>>({
      id: new FormControl(item?.id ?? generateGuid()),
      projectId: new FormControl(item?.projectId),
      title: new FormControl(item?.title),
      fileId: new FormControl(item?.fileId ?? ''),
      file: this.initFileForm(item?.file),
      width: new FormControl(item?.width ?? 0, Validators.required),
      height: new FormControl(item?.height ?? 0, Validators.required),
      titleBlockX: new FormControl(item?.titleBlockX ?? 0, Validators.required),
      titleBlockY: new FormControl(item?.titleBlockY ?? 0, Validators.required),
      titleBlockWidth: new FormControl(item?.titleBlockWidth ?? 0, Validators.required),
      titleBlockHeight: new FormControl(item?.titleBlockHeight ?? 0, Validators.required),
      scale: new FormControl(item?.scale ?? 0, Validators.required),
      constructionAreaLocations: new FormArray(
        item?.constructionAreaLocations?.map((loc) => FormGenerator.initConstructionAreaLocationForm(loc)) ?? []
      ),
      familyKey: new FormControl(item?.familyKey),
      currentVersionId: new FormControl(item?.currentVersionId),
    });
    return form;
  }

  public static initConstructionAreaLocationForm(
    item: ConstructionAreaLocation | undefined
  ): FormGroup<FormGroupOf<ConstructionAreaLocation>> {
    const form = new FormGroup<FormGroupOf<ConstructionAreaLocation>>({
      id: new FormControl(item?.id ?? generateGuid()),
      constructionAreaId: new FormControl(item?.constructionAreaId ?? '', Validators.required),
      points: new FormArray(item?.points?.map((p) => FormGenerator.initPolygonPointForm(p)) ?? []),
      name: new FormControl(item?.name),
    });
    return form;
  }

  public static initPolygonPointForm(item: PolygonPoint | undefined): FormGroup<FormGroupOf<PolygonPoint>> {
    return new FormGroup<FormGroupOf<PolygonPoint>>({
      x: new FormControl<number | null>(item?.x ?? 0, Validators.required),
      y: new FormControl<number | null>(item?.y ?? 0, Validators.required),
    });
  }

  public static initApiKeyForm(apiKey?: ApiKey, allScopes: ApiKeyScope[] = []): FormGroup<FormGroupOf<ApiKeyRequest>> {
    const accessByScope = new Map((apiKey?.permissions ?? []).map((x) => [x.scope, x.accessLevel]));

    return new FormGroup<FormGroupOf<ApiKeyRequest>>({
      name: new FormControl(apiKey?.name ?? null, [Validators.required, Validators.maxLength(100)]),
      description: new FormControl(apiKey?.description ?? null, Validators.maxLength(500)),
      expiration: new FormControl(ApiKeyExpiration.Days90, Validators.required),
      permissions: new FormArray(
        allScopes.map(
          (scope) =>
            new FormGroup<FormGroupOf<ApiKeyPermission>>({
              scope: new FormControl(scope),
              accessLevel: new FormControl(accessByScope.get(scope) ?? ApiKeyAccessLevel.None),
            })
        ),
        (control) =>
          (control.value as ApiKeyPermission[]).some((x) => x.accessLevel !== ApiKeyAccessLevel.None)
            ? null
            : { noPermissionSelected: true }
      ),
    });
  }
}

const updateSocialCostFactorLaborCost = (
  translateService: TranslateService,
  form: FormGroup<FormGroupOf<Project>> | FormGroup<FormGroupOf<Company>>
) => {
  const socialCostsFactor = form.controls?.labourCosts?.controls?.socialCostsFactor?.value;

  if (!!form?.controls?.priceFactors?.controls && !!socialCostsFactor) {
    let readonlyPriceFactor = form.controls.priceFactors.controls.find((x) => x.value.readonly);
    if (readonlyPriceFactor) {
      readonlyPriceFactor.controls.factor.setValue(socialCostsFactor, { onlySelf: true, emitEvent: false });
      readonlyPriceFactor.disable({ onlySelf: true, emitEvent: false });
    } else {
      readonlyPriceFactor = FormGenerator.initPriceFactors(
        translateService,
        null,
        PriceFactorType.UnionFactor,
        translateService.instant('project.labourCosts.socialCostsFactor'),
        socialCostsFactor,
        true
      );
      readonlyPriceFactor.disable({ onlySelf: true, emitEvent: false });
      form?.controls?.priceFactors.controls.splice(0, 0, readonlyPriceFactor);
    }
  }
};

function getProductPackageItemProductControls(controls: FormGroupOf<ProductPackageItem>) {
  return [
    controls.specialTreatmentCosts,
    controls.labourCostMethod,
    controls.baseSalaryPerUnit,
    controls.baseUnitTime,
    controls.unitCost,
    controls.additionalCostsPercentage,
    controls.additionalSubcontractorCosts,
    controls.additionalLabourCostsPercentage,
  ];
}

function updateLabourCostsMethodControls(
  controls: {
    baseUnitTime?: FormControl<number | null | undefined>;
    baseSalaryPerUnit?: FormControl<Money | null | undefined>;
  },
  enabled: boolean,
  method: LabourCostMethod
): void {
  if (enabled && method === LabourCostMethod.InstallationTime) {
    controls.baseUnitTime?.enable({ onlySelf: true, emitEvent: false });
  } else {
    controls.baseUnitTime?.disable({ onlySelf: true, emitEvent: false });
  }

  if (enabled && method === LabourCostMethod.UnionPrice) {
    controls.baseSalaryPerUnit?.enable({ onlySelf: true, emitEvent: false });
  } else {
    controls.baseSalaryPerUnit?.disable({ onlySelf: true, emitEvent: false });
  }
}
